import {
  getScrollableParent,
  getYOffsetOutsideOfVisibleScroll,
  type CollisionInsertPosition,
} from "@skilitics/designer-ui-components";
import {
  closestCenter,
  pointerIntersection,
  type Collision,
  type CollisionDetector,
  type Coordinates,
  type UniqueIdentifier,
} from "@skilitics/drag-drop";

import type { Orientation } from "../types.ts";
import { DND_CANVAS_EDITOR_ID, DND_CANVAS_SIDEBAR_ID, DND_CANVAS_SIDEBAR_LAYERS_ID } from "./dndConstants.ts";
import { collisionDataSchema, draggableDataSchema, type CollisionData } from "./dndSchemas.ts";
import { isResizeableId } from "./dndUtils.ts";

const getDefaultInsertPosition = (orientation: Orientation): CollisionInsertPosition => {
  return orientation === "horizontal" ? "insertLeft" : "insertTop";
};

const checkIfPointerWithinCanvasSidebar = (pointerCoordinates: Coordinates) => {
  const canvasSidebarElement = document.getElementById(DND_CANVAS_SIDEBAR_ID);
  return !!canvasSidebarElement && isPointWithinRect(pointerCoordinates, canvasSidebarElement.getBoundingClientRect());
};

const checkIfPointerWithinLayersPanel = (pointerCoordinates: Coordinates) => {
  const canvasSidebarElement = document.getElementById(DND_CANVAS_SIDEBAR_LAYERS_ID);
  return !!canvasSidebarElement && isPointWithinRect(pointerCoordinates, canvasSidebarElement.getBoundingClientRect());
};

const checkIfPointerWithinEditorArea = (pointerCoordinates: Coordinates) => {
  const canvasElement = document.getElementById(DND_CANVAS_EDITOR_ID);
  return !!canvasElement && isPointWithinRect(pointerCoordinates, canvasElement.getBoundingClientRect());
};

// Check if a given point is contained within a bounding rectangle
const isPointWithinRect = (point: Coordinates, rect: DOMRect): boolean => {
  const { top, left, bottom, right } = rect;
  return top <= point.y && point.y <= bottom && left <= point.x && point.x <= right;
};

export type CanvasCollisionDetectorOptions = {
  inLayersPanel: boolean;
  orientation: Orientation;
  hasChildren?: boolean;
  typeId: string;
  ancestorIds: number[];
  isAbsolutelyPositioned?: boolean;
};

// There are 5 possible scenarios for collisions:
// 1. Dragging from components panel to canvas.
// 2. Dragging from components panel to layers panel.
// 3. Dragging components on the canvas.
// 4. Dragging layers in the layers panel.
// 5. Dragging "absolute" positioned elements within the same parent container.

/**
 * Factory that creates a per-droppable collision detector with context baked in.
 * Each droppable knows its own panel, orientation, typeId etc. so there is no need
 * to read and parse droppable data during the collision hot-path.
 */
export const createCanvasCollisionDetector = ({
  inLayersPanel,
  orientation,
  hasChildren,
  typeId,
  ancestorIds,
  isAbsolutelyPositioned: droppableIsAbsolutelyPositioned,
}: CanvasCollisionDetectorOptions): CollisionDetector => {
  return (input) => {
    const { droppable, dragOperation } = input;
    const sourceId = dragOperation.source?.id;

    if (sourceId != null && isResizeableId(sourceId)) return null;

    const pointerCoordinates = dragOperation.position?.current ?? null;
    if (!pointerCoordinates) return null;
    if (!checkIfPointerWithinEditorArea(pointerCoordinates)) return null;

    // Panel matching: pointer must be in the same panel as the droppable
    if (inLayersPanel) {
      if (!checkIfPointerWithinLayersPanel(pointerCoordinates)) return null;
    } else {
      if (checkIfPointerWithinCanvasSidebar(pointerCoordinates)) return null;
    }

    // Try pointer intersection first
    const pointerCollision = pointerIntersection(input);

    if (pointerCollision) {
      const droppableRect = droppable.shape?.boundingRectangle;
      const element = "element" in droppable ? (droppable as { element?: Element }).element : undefined;

      const parsedDraggableData = draggableDataSchema.safeParse(dragOperation.source?.data);
      if (!parsedDraggableData.success) return null;

      const {
        node: draggableNode,
        inLayersPanel: activeInLayersPanel,
        isAbsolutelyPositioned,
      } = parsedDraggableData.data;

      if (
        !droppableRect ||
        // Check if droppable container is visible in scroll container.
        // Only relevant for the layers panel — on the canvas, pointerIntersection already
        // validates visibility via viewport coordinates, and this check produces false negatives
        // for absolutely positioned elements (and their children) outside parent bounds.
        (inLayersPanel &&
          !!getYOffsetOutsideOfVisibleScroll(
            (element ?? null) as HTMLElement | null,
            getScrollableParent((element ?? null) as HTMLElement | null),
          )) ||
        // Disable moving a container into a descendant of itself
        (draggableNode.type === "instance" && ancestorIds?.includes(draggableNode.id)) ||
        // If active draggable is in the layers panel, then the droppable container must be in the layers panel
        (activeInLayersPanel && !inLayersPanel)
      ) {
        return null;
      }

      let indicator = getDefaultInsertPosition(orientation);

      const mousePosition = orientation === "horizontal" ? pointerCoordinates.x : pointerCoordinates.y;
      // Keep center area for flexbox containers to allow dropping "inside",
      // otherwise simply place "before" or "after"
      const divisor = typeId === "flexbox.container" && !(inLayersPanel && hasChildren) ? 4 : 2;

      const before =
        orientation === "vertical"
          ? droppableRect.top + droppableRect.height / divisor
          : droppableRect.left + droppableRect.width / divisor;
      const after =
        orientation === "vertical"
          ? droppableRect.top + droppableRect.height - droppableRect.height / divisor
          : droppableRect.left + droppableRect.width - droppableRect.width / divisor;

      // Prefer deeper elements over ancestors when both detect a pointer intersection.
      // pointerIntersection uses 1/distance as the value, so parents often win by proximity to center.
      const depthBias = ancestorIds.length;

      // If dragging into the layers panel, and the droppable is a container with children,
      // show the indicator as "before" or "inside" (not after!), due to the display of child layers.

      if (typeId === "flexbox.container" && hasChildren && inLayersPanel) {
        indicator = mousePosition < before ? "insertTop" : "inside";
        return {
          ...pointerCollision,
          value: pointerCollision.value + depthBias,
          data: { ...pointerCollision.data, indicator, element } satisfies CollisionData,
        };
      }

      if (isAbsolutelyPositioned || droppableIsAbsolutelyPositioned) {
        indicator = "inside";
      } else if (mousePosition < before) {
        indicator = orientation === "vertical" ? "insertTop" : "insertLeft";
      } else if (mousePosition > after) {
        indicator = orientation === "vertical" ? "insertBottom" : "insertRight";
      } else {
        indicator = "inside";
      }

      return {
        ...pointerCollision,
        value: pointerCollision.value + depthBias,
        data: { ...pointerCollision.data, indicator, element } satisfies CollisionData,
      };
    }

    // Fallback: allows dropping into "empty" space at the tail end of layer items
    if (inLayersPanel) {
      const closestCollision = closestCenter(input);
      if (closestCollision) {
        const droppableRect = droppable.shape?.boundingRectangle;
        const element = "element" in droppable ? (droppable as { element?: Element }).element : undefined;
        let indicator = getDefaultInsertPosition(orientation);
        if (droppableRect) {
          const center =
            orientation === "vertical"
              ? droppableRect.top + droppableRect.height / 2
              : droppableRect.left + droppableRect.width / 2;
          const mousePosition = orientation === "vertical" ? pointerCoordinates.y : pointerCoordinates.x;
          indicator =
            mousePosition > center
              ? orientation === "vertical"
                ? "insertBottom"
                : "insertRight"
              : orientation === "vertical"
                ? "insertTop"
                : "insertLeft";
        }
        return { ...closestCollision, data: { ...closestCollision.data, indicator, element } satisfies CollisionData };
      }
    }

    return null;
  };
};

export const getInsertPosition = (collisions: Collision[] | null, activeId: UniqueIdentifier | undefined) => {
  if (collisions?.length) {
    const { data, id: collisionId } = collisions[0]; // createCanvasCollisionDetector won't return more than one collision.
    const collisionData = collisionDataSchema.safeParse(data);

    if (collisionId !== activeId && collisionData.success) {
      return collisionData.data.indicator;
    }
  }
  return undefined;
};
